import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';

import {
  Breakpoint,
  BreakpointRecord,
  MarginRecord,
  NormalizedSpacingKey,
  NormalizedSpacingRecord,
  PaddingRecord,
  Spacing,
  SpacingRecord,
  SpacingType,
  SpacingValue,
} from '../theme/type';
import { createMarginRecord, createPaddingRecord } from '../theme/utils';

export default function spacing<T extends SpacingType>(
  type: T,
  value: Spacing,
  ...fallbacks: Spacing[]
): T extends SpacingType.Margin ? MarginRecord : PaddingRecord {
  const values: NormalizedSpacingRecord = normalize(value);

  fallbacks.forEach(fallback => {
    const fb: NormalizedSpacingRecord = normalize(fallback);
    Object.values(NormalizedSpacingKey).forEach(key => {
      if (values[key] === undefined && fb[key] !== undefined) values[key] = cloneDeep(fb[key]);
    });
  });

  if (type === SpacingType.Margin) {
    return createMarginRecord(values) as T extends SpacingType.Margin
      ? MarginRecord
      : PaddingRecord;
  } else {
    return createPaddingRecord(values) as T extends SpacingType.Margin
      ? MarginRecord
      : PaddingRecord;
  }
}

export function getMargin(...values: (Spacing | undefined)[]): MarginRecord {
  const [value = {}, ...rest] = values;
  const fallbacks = rest.filter(fb => fb !== undefined);
  return spacing<SpacingType.Margin>(SpacingType.Margin, value, ...fallbacks);
}

export function getPadding(...values: (Spacing | undefined)[]): PaddingRecord {
  const [value = {}, ...rest] = values;
  const fallbacks = rest.filter(fb => fb !== undefined);
  return spacing<SpacingType.Padding>(SpacingType.Padding, value, ...fallbacks);
}

function normalize(value: Spacing): NormalizedSpacingRecord {
  // 1. handle SpacingValue
  if (typeof value === 'string' || isBreakpointsRecord(value)) {
    return createSpacingRecord(value, Object.values(NormalizedSpacingKey));
  }

  // 2. handle SpacingRecord
  const result: NormalizedSpacingRecord = {};

  // 2.1 handle SpacingKey.X and SpacingKey.Y
  if (value.x !== undefined) {
    const horizontalKeys = [NormalizedSpacingKey.Left, NormalizedSpacingKey.Right];
    Object.assign(result, createSpacingRecord(value.x, horizontalKeys));
  }
  if (value.y !== undefined) {
    const verticalKeys = [NormalizedSpacingKey.Top, NormalizedSpacingKey.Bottom];
    Object.assign(result, createSpacingRecord(value.y, verticalKeys));
  }

  // 2.2 handle NormalizedSpacingRecord
  // left, top, width, height is more specific than x, y
  // therefore override values if exists, otherwise use x, y from above
  Object.values(NormalizedSpacingKey).forEach(key => {
    if (value[key] !== undefined) result[key] = clone(value[key]);
  });

  return result;
}

function isBreakpointsRecord(value: BreakpointRecord | SpacingRecord): value is BreakpointRecord {
  return Object.keys(value).some(key => Object.values(Breakpoint).includes(key as Breakpoint));
}

function createSpacingRecord(
  value: SpacingValue,
  keys: NormalizedSpacingKey[]
): NormalizedSpacingRecord {
  return Object.fromEntries(keys.map(key => [key, clone(value)])) as NormalizedSpacingRecord;
}
